import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { SearchCriteria, SearchResult } from '../..';
import { AuthorizeImpersonationRequest } from '../impersonation/models/authorize-impersonation-request';
import { AuthorizeImpersonationResponse } from '../impersonation/models/authorize-impersonation-response';
import { ImpersonationRepository } from '../impersonation/impersonation.repository';
import { CreateImpersonationHistoryRequest } from '../impersonation/models/create-impersonation-history-request';
import { ImpersonationAuthorizationDetailsDto } from '../impersonation/models/impersonation-authorize-details';
import { ImpersonationUserDetails } from '../impersonation/models/impersonation-user-details';
import { ImpersonationHistoryResponse } from '../impersonation/models/impersonation-history-response';
import { UserImpersonationAuth } from '../impersonation/models/user-impersonation-auth.model';
import { JsonPatchDocument } from '../impersonation/models/json-patch-operation.model';
import { ImpersonationActionLogRequest } from '../impersonation/models/impersonation-action-log.model';
import { SKIP_IMPERSONATION_LOG_METADATA } from '../impersonation/impersonation.constants';

@Injectable()
export class DefaultImpersonationRepository implements ImpersonationRepository {
  readonly IMPERSONATION_API_BASE = '/api/identity/v1/impersonation';

  constructor(private http: HttpClient) {}

  authorizeImpersonation(request: AuthorizeImpersonationRequest): Observable<AuthorizeImpersonationResponse> {
    return this.http.post<AuthorizeImpersonationResponse>(`${this.IMPERSONATION_API_BASE}/authorize`, request, {
      context: new HttpContext().set(SKIP_IMPERSONATION_LOG_METADATA, true)
    });
  }

  getImpersonationDetails(): Observable<ImpersonationAuthorizationDetailsDto> {
    return this.http.get<ImpersonationAuthorizationDetailsDto>(`${this.IMPERSONATION_API_BASE}`);
  }

  getImpersonationEligibleUsers(
    criteria: SearchCriteria,
    sort?: SortDescriptor
  ): Observable<SearchResult<ImpersonationUserDetails>> {
    let params = new HttpParams();
    if (criteria.key != null) params = params.set('key', criteria.key);
    if (criteria.skip != null) params = params.set('skip', criteria.skip.toString());
    if (criteria.take != null) params = params.set('take', criteria.take.toString());
    if (sort) {
      if (sort.field != null) params = params.set('sortField', sort.field);
      if (sort.dir != null) params = params.set('sortDirection', sort.dir);
    }

    return this.http.get<SearchResult<ImpersonationUserDetails>>(`${this.IMPERSONATION_API_BASE}/users`, {
      params: params
    });
  }

  getUsers(
    criteria: SearchCriteria,
    includeDeleted: boolean = false,
    sort?: SortDescriptor
  ): Observable<SearchResult<UserImpersonationAuth>> {
    let params = new HttpParams().set('includeDeleted', includeDeleted);
    if (criteria.key) params = params.set('key', criteria.key);
    if (criteria.skip) params = params.set('skip', criteria.skip.toString());
    if (criteria.take) params = params.set('take', criteria.take.toString());
    if (sort) {
      params = params.set('sortField', `${sort.field}`);
      params = params.set('sortDirection', `${sort.dir}`);
    }

    return this.http.get<SearchResult<UserImpersonationAuth>>(
      `${this.IMPERSONATION_API_BASE}/impersonation-authorizations`,
      { params: params }
    );
  }

  getImpersonationHistory(
    authId: number,
    criteria: SearchCriteria
  ): Observable<SearchResult<ImpersonationHistoryResponse>> {
    let params = new HttpParams();
    if (criteria.skip != null) params = params.set('skip', criteria.skip.toString());
    if (criteria.take != null) params = params.set('take', criteria.take.toString());

    return this.http.get<SearchResult<ImpersonationHistoryResponse>>(
      `${this.IMPERSONATION_API_BASE}/history/${authId}`,
      {
        params: params
      }
    );
  }

  recordImpersonationHistory(request: CreateImpersonationHistoryRequest): Observable<number> {
    return this.http.post<number>(`${this.IMPERSONATION_API_BASE}/history`, request, {
      context: new HttpContext().set(SKIP_IMPERSONATION_LOG_METADATA, true)
    });
  }

  updateImpersonationEndDate(id: number, patchRequest: JsonPatchDocument): Observable<void> {
    return this.http.patch<void>(`${this.IMPERSONATION_API_BASE}/history/${id}`, patchRequest, {
      context: new HttpContext().set(SKIP_IMPERSONATION_LOG_METADATA, true)
    });
  }

  logImpersonationAction(request: ImpersonationActionLogRequest): Observable<void> {
    return this.http.post<void>(`${this.IMPERSONATION_API_BASE}/action`, request, {
      context: new HttpContext().set(SKIP_IMPERSONATION_LOG_METADATA, true)
    });
  }
}
